import { setHours, setMinutes, subDays, setSeconds } from 'date-fns';
// Returns a function, that, as long as it continues to be invoked, will not
// be triggered. The function will be called after it stops being called for
// N milliseconds. If `immediate` is passed, trigger the function on the
// leading edge, instead of the trailing.
/* istanbul ignore file */
export function debounce(func, wait, immediate) {
  var timeout;
  return function () {
    var context = this,
      args = arguments;
    var later = function () {
      timeout = null;
      if (!immediate) func.apply(context, args);
    };
    var callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    if (callNow) func.apply(context, args);
  };
}

export let isFunction = (obj) => {
  return obj && {}.toString.call(obj) === '[object Function]';
};

/**
 * Validating Uniqueness
 * @param dto - The DTO to check against
 * @param allEntities - The list of entities which will be searched
 * @param key - The key of the property we're checking
 * @param val - The value the user entered
 * @param lowerCase - Compares using the toLowerCase() function
 * @returns {boolean}
 */
export function isUnique(dto, allEntities, key, val, lowerCase = true) {
  const isUnique = !allEntities?.find((entity) => {
    if (lowerCase) {
      if (dto?.[key]?.toLowerCase() === val?.toLowerCase()) return false;
      return entity[key]?.toLowerCase() === val?.toLowerCase();
    } else {
      if (dto?.[key] === val) return false;
    }

    return entity[key] === val;
  });
  return isUnique;
}

export function uuidv4() {
  return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c) =>
    (c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16),
  );
}

export const setStartDate = (fromDate, displayDays) => {
  const endDate = new Date(fromDate);
  const newStart = setMinutes(
    setHours(subDays(endDate, displayDays), endDate.getHours()),
    endDate.getMinutes(),
  ).getTime();

  return newStart;
};

export const configStartDate = (fromDate, displayRange) => {
  const endDate = new Date(fromDate);
  const newStart = setSeconds(setMinutes(setHours(subDays(fromDate, displayRange), 0), 0), 0).getTime();

  return newStart;
};

export const roundDateDownToHour = (myDate) => {
  const newDate = new Date(myDate);
  newDate.setMinutes(0, 0, 0); // Resets minutes, seconds and milliseconds

  return newDate;
};

/**
 * Calculates the number of days between two epoch timestamps, rounding up to the nearest whole day.
 * This function is useful for determining the total number of days covering a given time span.
 *
 * @param {number} min - The starting epoch timestamp (in milliseconds).
 * @param {number} max - The ending epoch timestamp (in milliseconds).
 * @returns {number} The number of days, rounded up, between the two timestamps.
 */
export const daysBetweenEpochsRoundedUp = (min, max) => {
  const millisecondsPerDay = 1000 * 60 * 60 * 24; // Number of milliseconds in one day
  const differenceMs = Math.abs(min - max); // Absolute difference in milliseconds between two times
  const days = Math.ceil(differenceMs / millisecondsPerDay); // Calculating days and rounding up
  return days;
};

/**
 * Computes the midpoint epoch timestamp between two other epoch timestamps.
 * This is typically used to find a central reference time between two points.
 *
 * @param {number} min - The starting epoch timestamp (in milliseconds).
 * @param {number} max - The ending epoch timestamp (in milliseconds).
 * @returns {number} The epoch timestamp of the midpoint.
 */
export const midpointBetweenEpochs = (min, max) => {
  const midpointTimestamp = new Date((min + max) / 2); // Calculating the midpoint by averaging the timestamps
  return midpointTimestamp.getTime(); // Returning the timestamp of the midpoint date
};

/**
 * Modifies a given timestamp by a specified number of days, which can be positive or negative.
 * This function allows for easy calculation of dates ahead or behind a given reference point.
 *
 * @param {number} timestamp - The original epoch timestamp (in milliseconds) to modify.
 * @param {number} daysToModify - The number of days to add (positive) or subtract (negative) from the timestamp.
 * @returns {number} The new epoch timestamp after adding or subtracting the specified number of days.
 */
export const addOrSubtractDaysToTimestamp = (timestamp, daysToModify) => {
  const millisecondsPerDay = 1000 * 60 * 60 * 24; // Number of milliseconds in one day
  const millisecondsToModify = daysToModify * millisecondsPerDay; // Total milliseconds to add or subtract
  const newTimestamp = timestamp + millisecondsToModify; // Calculating the new timestamp
  return newTimestamp;
};
/**
 * Modifies a given timestamp by a specified number of hours, which can be positive or negative.
 * This function allows for easy calculation of dates ahead or behind a given reference point.
 *
 * @param {number} timestamp - The original epoch timestamp (in milliseconds) to modify.
 * @param {number} hursToModify - The number of hours to add (positive) or subtract (negative) from the timestamp.
 * @returns {number} The new epoch timestamp after adding or subtracting the specified number of hours.
 */
export const addOrSubtractHoursToTimestamp = (timestamp, hoursToModify) => {
  const millisecondsPerHour = 1000 * 60 * 60; // Number of milliseconds in one hour
  const millisecondsToModify = hoursToModify * millisecondsPerHour; // Total milliseconds to add or subtract
  const newTimestamp = timestamp + millisecondsToModify; // Calculating the new timestamp
  return newTimestamp;
};

/**
 * Checks if two date ranges overlap.
 *
 * @param {string} start1 - The start date of the first range (ISO 8601 format or any valid date string).
 * @param {string} end1 - The end date of the first range.
 * @param {number} end2 - The end date of the second range (epoch time in milliseconds).
 * @param {number} [dataRange=0] - The duration (in days) of the second range, used to calculate its start date.
 * @returns {boolean} - Returns true if the date ranges overlap, otherwise false.
 */
export const dateRangesOverlap = (start1, end1, end2, dataRange) => {
  // Convert start1 and end1 (string dates) to epoch timestamps in milliseconds
  const start1Epoch = new Date(start1).getTime();
  const end1Epoch = new Date(end1).getTime();
  const start2 = end2 - (dataRange ?? 0) * 24 * 60 * 60 * 1000;

  // Check if the two ranges overlap
  return start1Epoch <= end2 && end1Epoch >= start2;
};

export const epochToISOString = (epochMs) => {
  return new Date(epochMs).toISOString();
};

export const base64ToBlobUrl = (base64, contentType = 'image/jpeg') => {
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], { type: contentType });
  const blobUrl = URL.createObjectURL(blob);
  return blobUrl;
};
